import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages, ChevronDown, Check } from "lucide-react";
import { api } from "@/lib/api";

const LANGS = [
  { code: "fr", label: "FR" },
  { code: "en", label: "EN" },
  { code: "de", label: "DE" },
  { code: "es", label: "ES" },
  { code: "it", label: "IT" },
  { code: "pt", label: "PT" },
];

/**
 * Compact language dropdown — replaces the previous trio of FR / EN / DE
 * side-by-side buttons. Used in topbar, public pages, login & forgot-password.
 *
 * The exposed data-testids are kept identical (`language-switcher`,
 * `lang-btn-{code}`) so existing E2E tests keep working ; the trigger now
 * carries `language-switcher-trigger`.
 */
export default function LanguageSwitcher({ compact = false, className = "" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on click-outside or Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const change = async (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
    try { window.localStorage.setItem("noam_lang", code); } catch {}
    // Persist to the user's profile so /auth/me does not revert the choice
    // on the next navigation. Best-effort — silently ignore failures (e.g.
    // anonymous demo visitors / login page).
    try { await api.patch("/auth/me/preferences", { locale: code }); } catch {}
  };

  const activeCode = (i18n.language || "fr").slice(0, 2);
  const active = LANGS.find((l) => l.code === activeCode) || LANGS[0];

  return (
    <div
      ref={rootRef}
      className={`relative inline-block ${className}`}
      data-testid="language-switcher"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-medium tracking-wider transition-colors ${
          open
            ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-white"
            : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-[#378ADD]/40 hover:text-white"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Langue"
        data-testid="language-switcher-trigger"
      >
        {!compact && <Languages className="w-3.5 h-3.5 text-zinc-400" />}
        <span>{active.label}</span>
        <ChevronDown className={`w-3 h-3 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 noam-card py-1 min-w-[110px] z-50"
          role="listbox"
          data-testid="language-switcher-menu"
        >
          {LANGS.map((l) => {
            const isActive = activeCode === l.code;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => change(l.code)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs transition-colors ${
                  isActive ? "text-[#378ADD] bg-[#378ADD]/10" : "text-zinc-300 hover:bg-white/5"
                }`}
                data-testid={`lang-btn-${l.code}`}
              >
                <span className="font-medium">{l.label}</span>
                {isActive && <Check className="w-3 h-3" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
